import { useState, useRef, useEffect, useContext } from 'react';
import { RxAccessibility } from 'react-icons/rx';
import AccessibilityDrawerSettings from '../../views/settings/AccessibilityDrawerSettings';
import { UserContext } from "../../context/UserProvider";

export default function AccessibilityButton() {
  const {
    setUserPreference,
    accessibilityButtonPosition,
    setAccessibilityButtonPosition,
  } = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(accessibilityButtonPosition || window.innerHeight / 2 - 40);
  const buttonRef = useRef(null);
  const modalRef = useRef(null);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const offsetY = useRef(0);
  const currentY = useRef(position);

  useEffect(() => {
    if (accessibilityButtonPosition !== undefined) {
      setPosition(accessibilityButtonPosition);
      currentY.current = accessibilityButtonPosition;
      if (buttonRef.current) {
        buttonRef.current.style.top = `${accessibilityButtonPosition}px`;
      }
    }
  }, [accessibilityButtonPosition]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isDragging.current) return;

      let newY = event.clientY - offsetY.current;
      const minY = 80;
      const maxY = window.innerHeight - 80;

      if (newY < minY) newY = minY;
      if (newY > maxY - 64) newY = maxY - 64;

      currentY.current = newY;
      if (buttonRef.current) {
        buttonRef.current.style.top = `${newY}px`;
      }
    };

    const handleMouseUpGlobal = (event) => {
      const hasMoved = Math.abs(currentY.current - startY.current) > 5;
      if (isDragging.current && hasMoved) {
        setPosition(currentY.current);
        setAccessibilityButtonPosition(currentY.current);
        setUserPreference({ newAccessibilityButtonPosition: currentY.current });
      } else if (isDragging.current && !hasMoved) {
        setIsOpen((prev) => !prev);
      }
      isDragging.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUpGlobal);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, [setUserPreference, setAccessibilityButtonPosition]);

  const handleMouseDown = (event) => {
    offsetY.current = event.clientY - buttonRef.current.getBoundingClientRect().top;
    startY.current = currentY.current;
    isDragging.current = true;
  };

  const handleMouseUp = () => {
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <button
        id="accessibility-button"
        ref={buttonRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        className="fixed right-0 z-50 flex h-16 w-14 items-center justify-center rounded-l-xl bg-blue-600 text-white shadow-xl transition-transform duration-200 hover:scale-105 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        style={{ top: `${position}px` }}
      >
        <RxAccessibility className="h-7 w-7" />
      </button>

      {isOpen && (
        <div id="accessibility-modal" className="fixed inset-0 z-50 flex items-center justify-end bg-opacity-50">
          <div
            ref={modalRef}
            className="relative w-full max-w-lg h-full bg-white shadow-2xl dark:bg-stone-950 rounded-l-xl p-6"
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-600 transition-colors duration-200 hover:bg-gray-200 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-stone-700 dark:hover:text-white"
            >
              ✕
            </button>
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-300 w-full">
              Accessibility Settings
            </h2>
            <AccessibilityDrawerSettings onClose={() => setIsOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}